import React, { useState } from 'react';
import { OverlayTrigger, Popover, Form, Badge, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { FaFilter, FaSortDown, FaRedo, FaSortAlphaDown, FaSortAlphaUp } from 'react-icons/fa';
import Select from 'react-select';
import Slider from '@mui/material/Slider';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, getYear } from 'date-fns';
import fr from 'date-fns/locale/fr';

const FilterableHeader = ({
    column,
    type = 'text',
    currentValue,
    onFilterChange,
    onSort,
    options = [],
    min = 0,
    max = 1000000,
    currentSortBy = null,
    currentSortOrder = null,
    onResetComplete = null
}) => {
    const [useCustomPeriod, setUseCustomPeriod] = useState(false);

    const handleReset = () => {
        let emptyVal;
        if (type === 'amount') {
            emptyVal = [min, max];
        } else if (type === 'multi-select') {
            emptyVal = [];
        } else if (type === 'text') {
            emptyVal = [];
        } else {
            emptyVal = '';
        }

        if (onResetComplete) {
            onResetComplete(emptyVal);
        } else {
            onFilterChange(emptyVal);
            if (onSort) onSort(null);
        }
        setUseCustomPeriod(false);
    };

    const renderPopoverContent = () => {
        switch (type) {
            case 'text':
                return (
                    <div style={{ minWidth: '250px' }}>
                        {onSort && (
                            <div className="d-flex gap-2 mb-3 border-bottom pb-2">
                                <Button
                                    variant={currentSortBy && currentSortOrder === 'asc' ? 'success' : 'outline-secondary'}
                                    size="sm"
                                    className="w-50 d-flex align-items-center justify-content-center gap-2"
                                    onClick={() => {
                                        const newOrder = currentSortBy && currentSortOrder === 'asc' ? null : 'asc';
                                        onSort(newOrder);
                                    }}
                                    style={currentSortBy && currentSortOrder === 'asc' ? {
                                        backgroundColor: '#198754',
                                        borderColor: '#198754',
                                        color: 'white'
                                    } : {}}
                                >
                                    <FaSortAlphaDown /> A-Z
                                </Button>
                                <Button
                                    variant={currentSortBy && currentSortOrder === 'desc' ? 'success' : 'outline-secondary'}
                                    size="sm"
                                    className="w-50 d-flex align-items-center justify-content-center gap-2"
                                    onClick={() => {
                                        const newOrder = currentSortBy && currentSortOrder === 'desc' ? null : 'desc';
                                        onSort(newOrder);
                                    }}
                                    style={currentSortBy && currentSortOrder === 'desc' ? {
                                        backgroundColor: '#198754',
                                        borderColor: '#198754',
                                        color: 'white'
                                    } : {}}
                                >
                                    <FaSortAlphaUp /> Z-A
                                </Button>
                            </div>
                        )}

                        <Select
                            isMulti
                            options={options}
                            value={options.filter(o => (currentValue || []).includes(o.value))}
                            onChange={val => onFilterChange(val.map(v => v.value))}
                            placeholder="Rechercher..."
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            autoFocus
                        />
                    </div>
                );
            case 'date': {
                const start = currentValue?.start || '';
                const end = currentValue?.end || '';
                const today = new Date();
                const currentYear = getYear(today);

                const handleYearSelect = (e) => {
                    const year = parseInt(e.target.value);
                    if (!year) return;
                    const newStart = startOfYear(new Date(year, 0, 1));
                    const newEnd = endOfYear(new Date(year, 0, 1));
                    onFilterChange({ start: format(newStart, 'yyyy-MM-dd'), end: format(newEnd, 'yyyy-MM-dd') });
                };

                const handleMonthSelect = (e) => {
                    const monthIndex = parseInt(e.target.value);
                    if (isNaN(monthIndex)) return;
                    const referenceDate = (start && start !== '') ? new Date(start) : new Date();
                    const year = getYear(referenceDate);

                    const newStart = startOfMonth(new Date(year, monthIndex, 1));
                    const newEnd = endOfMonth(new Date(year, monthIndex, 1));
                    onFilterChange({ start: format(newStart, 'yyyy-MM-dd'), end: format(newEnd, 'yyyy-MM-dd') });
                };

                return (
                    <div style={{ minWidth: '300px' }}>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            <Badge bg="light" text="dark" className="border cursor-pointer p-2" onClick={() => {
                                const now = new Date();
                                onFilterChange({ start: format(now, 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') });
                            }} style={{ cursor: 'pointer' }}>Auj.</Badge>
                            <Badge bg="light" text="dark" className="border cursor-pointer p-2" onClick={() => {
                                const now = new Date();
                                onFilterChange({
                                    start: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
                                    end: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd')
                                });
                            }} style={{ cursor: 'pointer' }}>Semaine</Badge>
                            <Badge bg="light" text="dark" className="border cursor-pointer p-2" onClick={() => {
                                const now = new Date();
                                onFilterChange({
                                    start: format(startOfMonth(now), 'yyyy-MM-dd'),
                                    end: format(endOfMonth(now), 'yyyy-MM-dd')
                                });
                            }} style={{ cursor: 'pointer' }}>Ce mois</Badge>
                            <Badge bg="light" text="dark" className="border cursor-pointer p-2" onClick={() => {
                                const now = new Date();
                                onFilterChange({
                                    start: format(startOfYear(now), 'yyyy-MM-dd'),
                                    end: format(endOfYear(now), 'yyyy-MM-dd')
                                });
                            }} style={{ cursor: 'pointer' }}>Année</Badge>
                        </div>

                        <Form.Check
                            type="switch"
                            id="custom-period-switch"
                            label="Période"
                            checked={useCustomPeriod}
                            onChange={(e) => setUseCustomPeriod(e.target.checked)}
                            className="mb-3 text-primary"
                        />

                        {!useCustomPeriod && (
                            <Row className="g-2 mb-3">
                                <Col xs={6}>
                                    <Form.Select size="sm" onChange={handleMonthSelect} defaultValue="">
                                        <option value="" disabled>Mois...</option>
                                        {Array.from({ length: 12 }).map((_, i) => (
                                            <option key={i} value={i}>{format(new Date(2000, i, 1), 'MMMM', { locale: fr })}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xs={6}>
                                    <Form.Select size="sm" onChange={handleYearSelect} defaultValue="">
                                        <option value="" disabled>Année...</option>
                                        {Array.from({ length: (currentYear - 2020 + 2) }).map((_, i) => {
                                            const y = 2020 + i;
                                            return <option key={y} value={y}>{y}</option>
                                        })}
                                    </Form.Select>
                                </Col>
                            </Row>
                        )}

                        {useCustomPeriod && (
                            <div className="border-top pt-2 bg-light p-2 rounded">
                                <Form.Group className="mb-2">
                                    <Form.Label className="small text-muted mb-0">Du</Form.Label>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={start}
                                        max={end}
                                        onChange={e => {
                                            const newStart = e.target.value;
                                            if (end && newStart > end) return;
                                            onFilterChange({ ...currentValue, start: newStart });
                                        }}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label className="small text-muted mb-0">Au</Form.Label>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={end}
                                        min={start}
                                        onChange={e => {
                                            const newEnd = e.target.value;
                                            if (start && newEnd < start) return;
                                            onFilterChange({ ...currentValue, end: newEnd });
                                        }}
                                    />
                                </Form.Group>
                            </div>
                        )}
                    </div>
                );
            }
            case 'amount': {
                const range = currentValue || [min, max];
                const effectiveMax = Math.max(max, range[1] || max);

                const handleMinChange = (e) => {
                    const Val = Number(e.target.value);
                    if (Val < min) return;
                    if (Val > (range[1] || effectiveMax)) return;
                    onFilterChange([Val, range[1] || effectiveMax]);
                };

                const handleMaxChange = (e) => {
                    const Val = Number(e.target.value);
                    if (Val < range[0]) return;
                    onFilterChange([range[0], Val]);
                };

                return (
                    <div style={{ minWidth: '320px', padding: '10px' }}>
                        <div className="d-flex justify-content-between align-items-center gap-2 mb-4">
                            <Form.Group className="flex-grow-1 me-2">
                                <Form.Label className="text-muted small mb-1">Min (FCFA)</Form.Label>
                                <InputGroup size="sm">
                                    <Form.Control
                                        type="number"
                                        value={range[0] || 0}
                                        onChange={handleMinChange}
                                        min={min}
                                        className="fw-bold"
                                    />
                                </InputGroup>
                            </Form.Group>
                            <span className="text-muted mt-3">-</span>
                            <Form.Group className="flex-grow-1 ms-2">
                                <Form.Label className="text-muted small mb-1">Max (FCFA)</Form.Label>
                                <InputGroup size="sm">
                                    <Form.Control
                                        type="number"
                                        value={range[1] || effectiveMax}
                                        onChange={handleMaxChange}
                                        min={range[0]}
                                        className="fw-bold"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </div>
                        <Slider
                            value={range}
                            onChange={(e, val) => onFilterChange(val)}
                            min={min}
                            max={effectiveMax}
                            valueLabelDisplay="auto"
                            size="small"
                            sx={{
                                color: '#198754',
                                '& .MuiSlider-thumb': {
                                    boxShadow: '0 0 0 0px rgba(25, 135, 84, 0.16)',
                                    '&:hover': {
                                        boxShadow: '0 0 0 8px rgba(25, 135, 84, 0.16)',
                                    },
                                },
                            }}
                        />
                        <div className="d-flex justify-content-between x-small text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                            <span>{min.toLocaleString()}</span>
                            <span>{effectiveMax.toLocaleString()}</span>
                        </div>
                    </div>
                );
            }
            case 'multi-select':
                return (
                    <div style={{ minWidth: '200px' }}>
                        {options.map(opt => (
                            <Form.Check
                                key={opt.value}
                                type="checkbox"
                                id={`check-${column.dataField}-${opt.value}`}
                                label={opt.label}
                                checked={(currentValue || []).includes(opt.value)}
                                onChange={e => {
                                    const checked = e.target.checked;
                                    let newVal = [...(currentValue || [])];
                                    if (checked) newVal.push(opt.value);
                                    else newVal = newVal.filter(v => v !== opt.value);
                                    onFilterChange(newVal);
                                }}
                                className="mb-2"
                            />
                        ))}
                    </div>
                );
            default:
                return null;
        }
    };

    const isActive = () => {
        if (!currentValue) return false;
        if (Array.isArray(currentValue) && currentValue.length === 0) return false;
        if (type === 'amount' && currentValue[0] === min && currentValue[1] === max) return false;
        if (type === 'date' && !currentValue?.start && !currentValue?.end) return false;
        return true;
    };

    const popover = (
        <Popover id={`popover-${column.dataField}`} style={{ maxWidth: '350px' }}>
            <Popover.Header as="h3" className="d-flex justify-content-between align-items-center">
                {column.text}
                <FaRedo
                    className="text-secondary cursor-pointer"
                    title="Réinitialiser"
                    style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                    onClick={handleReset}
                />
            </Popover.Header>
            <Popover.Body>
                {renderPopoverContent()}
            </Popover.Body>
        </Popover>
    );

    return (
        <OverlayTrigger trigger="click" placement="bottom" overlay={popover} rootClose>
            <div className="d-flex align-items-center cursor-pointer" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <span className={isActive() ? "text-primary fw-bold" : ""}>{column.text}</span>
                <FaSortDown className={`ms-2 small ${isActive() ? "text-primary" : "text-muted"}`} />
                {isActive() && <FaFilter size={10} className="ms-1 text-primary" />}
            </div>
        </OverlayTrigger>
    );
};

export default FilterableHeader;
